import React from 'react';
import RichText from '@components/rich-text/rich-text';
import { H2Atom } from '@atoms/text-atoms/text-atoms';
import { GatsbyImage } from 'gatsby-plugin-image';
import { RichTextBlockProps } from './rich-text-block.types';
import {
  BackgroundColor,
  Container,
  RichTextWrapper,
  BackgroundImage,
  GifOrSvgImage,
  VideoWrapper,
} from './rich-text-block.styles';

const RichTextBlock: React.FC<RichTextBlockProps> = ({
  title,
  isFirstBlock = false,
  anchorId,
  richTextData,
  tooltipData,
  className,
  extraComponents,
  backgroundColor = 'transparent',
  backgroundImage,
  mobileBackgroundImage,
  colour,
  backgroundVideoUrl,
  videoBackgroundImage,
}) => {
  const resolveImage = (image: any) => {
    if (!image) return null;
    const { format, src } = image ?? {};
    const isUnsupportedType =
      format === 'image/gif' || format === 'image/svg+xml';

    return isUnsupportedType ? src : image.gatsbyImageData;
  };

  const desktopImage = resolveImage(backgroundImage);
  const mobileImage = resolveImage(mobileBackgroundImage);

  const hasBackgroundContent =
    backgroundColor !== 'transparent' ||
    !!backgroundImage ||
    !!mobileBackgroundImage;

  const hasDesktopBackgroundImage = !!desktopImage;
  const hasMobileBackgroundImage = !!mobileImage;

  const showVideo = !!backgroundVideoUrl;

  if (richTextData) {
    return (
      <Container
        colour={colour}
        backgroundColor={backgroundColor}
        hasDesktopBackgroundImage={hasDesktopBackgroundImage}
        hasMobileBackgroundImage={hasMobileBackgroundImage}
      >
        <BackgroundColor
          backgroundColor={backgroundColor}
          className={`
            ${hasMobileBackgroundImage ? 'hide-mobile' : ''}
            ${hasDesktopBackgroundImage ? 'hide-desktop' : ''}
          `}
        />

        {showVideo ? (
          <VideoWrapper data-testid="video">
            <video autoPlay loop muted playsInline src={backgroundVideoUrl} />
            <div className="video-image-bg">
              {videoBackgroundImage?.file && (
                <img
                  src={videoBackgroundImage.file.url}
                  alt={videoBackgroundImage.description || ''}
                />
              )}
            </div>
          </VideoWrapper>
        ) : (
          <>
            {hasDesktopBackgroundImage && (
              <BackgroundImage className="desktop-only">
                {typeof desktopImage === 'string' ? (
                  <GifOrSvgImage
                    src={desktopImage}
                    alt={backgroundImage?.alt || ''}
                  />
                ) : (
                  <GatsbyImage
                    image={desktopImage}
                    alt={backgroundImage?.alt || ''}
                    objectFit="cover"
                  />
                )}
              </BackgroundImage>
            )}

            {hasMobileBackgroundImage && (
              <BackgroundImage className="mobile-only">
                {typeof mobileImage === 'string' ? (
                  <GifOrSvgImage
                    src={mobileImage}
                    alt={mobileBackgroundImage?.alt || ''}
                  />
                ) : (
                  <GatsbyImage
                    image={mobileImage}
                    alt={mobileBackgroundImage?.alt || ''}
                    objectFit="cover"
                  />
                )}
              </BackgroundImage>
            )}
          </>
        )}

        <RichTextWrapper
          id={anchorId}
          className={className}
          applyPadding={hasBackgroundContent}
          removeTopPadding={isFirstBlock}
        >
          {title && <H2Atom>{title}</H2Atom>}
          <RichText
            content={richTextData}
            tooltipData={tooltipData}
            extraComponents={extraComponents}
          />
        </RichTextWrapper>
      </Container>
    );
  } else {
    console.error(
      `Rich Text Block called with invalid richTextData: ${richTextData}`
    );
    return null;
  }
};

export default RichTextBlock;
